var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
import { jsx, jsxs } from "react/jsx-runtime";
import { Component, useRef, useEffect } from "react";
import { emit } from "@zapier/events-toolkit-browser";
import { ZapierLogo, Button } from "@zapier/design-system";
function emitUserInteractionEvent(payload) {
  emit(
    {
      system_name: "marketing-site-layout",
      option_selected: "",
      ...payload
    },
    "web_analytics.tracking.user_interaction.UserInteractionEvent"
  );
}
class ErrorBoundary extends Component {
  constructor() {
    super(...arguments);
    __publicField(this, "state", {
      error: false
    });
  }
  static getDerivedStateFromError() {
    return { error: true };
  }
  componentDidCatch(error) {
    console.error(`[@zapier/marketing-site-layout] ${error}`);
    emitUserInteractionEvent({
      event_action: "error",
      custom_tracking_fields: {
        error,
        component: this.props.name
      }
    });
  }
  render() {
    return this.state.error ? this.props.fallback : this.props.children;
  }
}
function resolveURL(url, base = "") {
  url = url.replace(
    "{{path}}",
    typeof window === "undefined" ? "" : window.location.pathname
  );
  return base && url.startsWith("/") ? base + url : url;
}
function FallbackFooter(props) {
  return /* @__PURE__ */ jsx(
    "footer",
    {
      style: {
        width: "100%",
        gridArea: "footer",
        padding: "40px 20px",
        boxSizing: "border-box"
      },
      children: /* @__PURE__ */ jsxs(
        "div",
        {
          style: {
            maxWidth: "1200px",
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "20px 40px"
          },
          children: [
            /* @__PURE__ */ jsx("a", { "aria-label": "Zapier", href: resolveURL("/", props.baseURL), children: /* @__PURE__ */ jsx(ZapierLogo, { width: 104, height: 28 }) }),
            /* @__PURE__ */ jsxs(
              "div",
              {
                style: {
                  display: "flex",
                  gap: "12px",
                  fontSize: "14px",
                  color: "#574e4c"
                },
                children: [
                  /* @__PURE__ */ jsxs("div", { children: [
                    "© ",
                    (/* @__PURE__ */ new Date()).getFullYear(),
                    " Zapier Inc."
                  ] }),
                  /* @__PURE__ */ jsxs(
                    "div",
                    {
                      style: {
                        display: "flex",
                        gap: "12px",
                        textDecoration: "underline"
                      },
                      children: [
                        /* @__PURE__ */ jsx(
                          "div",
                          {
                            className: "ot-sdk-show-settings",
                            role: "button",
                            style: { display: "none" },
                            children: "Manage cookies"
                          }
                        ),
                        /* @__PURE__ */ jsx(
                          "a",
                          {
                            href: resolveURL("/legal", props.baseURL),
                            style: { color: "inherit" },
                            children: "Legal"
                          }
                        ),
                        /* @__PURE__ */ jsx(
                          "a",
                          {
                            href: resolveURL("/privacy", props.baseURL),
                            style: { color: "inherit" },
                            children: "Privacy"
                          }
                        )
                      ]
                    }
                  )
                ]
              }
            )
          ]
        }
      )
    }
  );
}
function FallbackHeader(props) {
  return /* @__PURE__ */ jsxs(
    "header",
    {
      style: {
        height: "56px",
        gridArea: "header",
        padding: "0 clamp(20px, 3vw, 40px)",
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        position: "sticky",
        top: 0,
        zIndex: "var(--zapier-site-header-z-index, 1000)",
        backgroundColor: "#fffdf9",
        borderBottom: "1px solid #ece9df"
      },
      children: [
        /* @__PURE__ */ jsx("a", { "aria-label": "Zapier", href: resolveURL("/", props.baseURL), children: /* @__PURE__ */ jsx(ZapierLogo, { width: 104, height: 28 }) }),
        /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: "4px" }, children: [
          /* @__PURE__ */ jsx(
            Button,
            {
              href: resolveURL("/app/login", props.baseURL),
              size: "small",
              color: "secondary",
              children: "Log in"
            }
          ),
          /* @__PURE__ */ jsx(Button, { href: resolveURL("/sign-up", props.baseURL), size: "small", children: "Sign up" })
        ] })
      ]
    }
  );
}
function Layout(props) {
  const root = useRef(null);
  const banner = useRef(null);
  useEffect(() => {
    var _a;
    if (!props.banner || !banner.current) {
      return (_a = root.current) == null ? void 0 : _a.style.setProperty("--zapier-banner-height", "0px");
    }
    const resizeObserver = new ResizeObserver(() => {
      var _a2, _b;
      (_b = root.current) == null ? void 0 : _b.style.setProperty(
        "--zapier-banner-height",
        `${((_a2 = banner.current) == null ? void 0 : _a2.clientHeight) ?? 0}px`
      );
    });
    resizeObserver.observe(banner.current);
    return () => resizeObserver.disconnect();
  }, [props.banner]);
  return /* @__PURE__ */ jsxs("div", { className: "zapier-marketing-site-layout", ref: root, children: [
    props.banner && /* @__PURE__ */ jsx("div", { className: "zapier-marketing-site-layout-banner", ref: banner, children: props.banner }),
    props.header && /* @__PURE__ */ jsx(
      ErrorBoundary,
      {
        name: "Header",
        fallback: /* @__PURE__ */ jsx(FallbackHeader, { baseURL: props.baseURL }),
        children: /* @__PURE__ */ jsx(
          props.header,
          {
            baseURL: props.baseURL,
            data: props.headerData,
            session: props.session,
            accounts: props.accounts,
            mainID: props.mainID
          }
        )
      }
    ),
    /* @__PURE__ */ jsx(
      "main",
      {
        className: "zapier-marketing-site-layout-main",
        id: props.mainID ?? "main",
        children: props.children
      }
    ),
    props.footer && /* @__PURE__ */ jsx(
      ErrorBoundary,
      {
        name: "Footer",
        fallback: /* @__PURE__ */ jsx(FallbackFooter, { baseURL: props.baseURL }),
        children: /* @__PURE__ */ jsx(
          props.footer,
          {
            baseURL: props.baseURL,
            data: props.footerData,
            showAlphabetNav: props.showAlphabetNav,
            showSEOLinks: props.showSEOLinks
          }
        )
      }
    )
  ] });
}
const MARKETING_SITE_LAYOUT_STYLESHEET = "https://cdn.zapier.com/marketing-site-layout/v1/marketing-site-layout.css";
let code;
async function loadMarketingSiteLayoutComponents({
  staging,
  dependencies
}) {
  var _a;
  const url = `https://cdn.${staging ? "zapier-staging" : "zapier"}.com/marketing-site-layout/v1/marketing-site-layout.umd.js`;
  const mod = { exports: {} };
  if (typeof window !== "undefined") {
    if ((_a = window.__REMOTE_PRELOAD) == null ? void 0 : _a[url]) {
      return window.__REMOTE_PRELOAD[url];
    }
    mod.exports = await new Promise((resolve, reject) => {
      Object.entries(dependencies).forEach(([key, value]) => {
        window[key] = value;
      });
      const script = document.createElement("script");
      script.src = url;
      script.onload = () => {
        resolve(window["@zapier/marketing-site-layout"]);
        document.head.removeChild(script);
      };
      script.onerror = () => {
        reject();
        document.head.removeChild(script);
      };
      document.head.appendChild(script);
    });
    window.__REMOTE_PRELOAD ?? (window.__REMOTE_PRELOAD = {});
    window.__REMOTE_PRELOAD[url] = mod.exports;
  } else {
    if (!code) {
      const response = await fetch(url);
      code = await response.text();
    }
    const evalRemoteModule = new Function("require", "module", "exports", code);
    const _require = (name) => {
      if (Object.hasOwn(dependencies, name)) {
        return dependencies[name];
      }
      throw new Error(`[loadLayoutRemoteModule] Module not found: ${name}`);
    };
    evalRemoteModule(_require, mod, mod.exports);
  }
  return mod.exports;
}
function withSSR(dynamic, loader) {
  const Header = dynamic(() => loader().then((mod) => mod.Header));
  const Footer = dynamic(() => loader().then((mod) => mod.Footer));
  if (typeof window !== "undefined") {
    const spiedFn = window.__NEXT_PRELOADREADY;
    window.__NEXT_PRELOADREADY = (...args) => {
      return loader().then(() => {
        Header.render.preload();
        Footer.render.preload();
      }).catch(() => {
      }).finally(() => spiedFn ? spiedFn(...args) : Promise.resolve());
    };
  }
  return { Header, Footer };
}
export {
  Layout,
  MARKETING_SITE_LAYOUT_STYLESHEET,
  loadMarketingSiteLayoutComponents,
  withSSR
};
